import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useE2EEChat } from '../hooks/useE2EEChat';
import { useChatStore, useTradeStore } from '../store';

export default function Chat() {
  const { roomId } = useParams<{ roomId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const { messages, isConnected, peerConnected, roomStatus } = useChatStore();
  const { role, status: tradeStatus } = useTradeStore();
  
  const { sendMessage, sendTradeAction, e2eeReady, fingerprint } = useE2EEChat({ 
    token, 
    roomId: roomId || '',
    enableE2EE: true,
  });

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="bg-telegram-secondary p-3 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-semibold">💬 P2P Chat</h1>
            <div className="text-xs text-telegram-hint">
              Room: {roomId?.slice(0, 8)}...
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${peerConnected ? 'bg-green-500' : 'bg-yellow-500'}`} />
            <span className="text-xs">
              {peerConnected ? 'Đối tác online' : 'Đang chờ...'}
            </span>
          </div>
        </div>
      </div>

      {/* Connection Status Banner */}
      {!isConnected && (
        <div className="bg-yellow-100 text-yellow-800 text-center py-2 text-sm">
          ⏳ Đang kết nối...
        </div>
      )}
      
      {/* E2EE Status Banner */}
      {isConnected && (
        <div className={`text-center py-1 text-xs ${e2eeReady ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
          {e2eeReady ? (
            <>🔐 Mã hoá E2E | Fingerprint: <code className="font-mono">{fingerprint}</code></>
          ) : (
            <>⏳ Đang thiết lập mã hoá...</>
          )}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-center text-telegram-hint py-8">
            <div className="text-4xl mb-2">🔒</div>
            <p>Chat được mã hoá end-to-end</p>
            <p className="text-xs mt-1">Không ai có thể đọc được tin nhắn</p>
          </div>
        )}
        
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.from === 'me' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                msg.from === 'me'
                  ? 'bg-telegram-button text-telegram-button-text rounded-br-none'
                  : 'bg-telegram-secondary rounded-bl-none'
              }`}
            >
              <p className="break-words">{msg.content}</p>
              <div className={`text-xs mt-1 ${
                msg.from === 'me' ? 'text-white/70' : 'text-telegram-hint'
              }`}>
                {new Date(msg.timestamp).toLocaleTimeString('vi-VN', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {msg.encrypted && ' 🔐'}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Trade Actions */}
      <div className="border-t bg-telegram-secondary p-2">
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button 
            onClick={() => sendTradeAction('confirm-payment')}
            className="whitespace-nowrap bg-blue-500 text-white px-3 py-1.5 rounded-full text-sm"
          >
            💸 Đã thanh toán
          </button>
          <button 
            onClick={() => sendTradeAction('release-crypto')}
            className="whitespace-nowrap bg-green-500 text-white px-3 py-1.5 rounded-full text-sm"
          >
            ✅ Thả crypto
          </button>
          <button 
            onClick={() => sendTradeAction('dispute')}
            className="whitespace-nowrap bg-red-500 text-white px-3 py-1.5 rounded-full text-sm"
          >
            ⚠️ Tranh chấp
          </button>
        </div>
      </div>

      {/* Input */}
      <div className="p-3 border-t bg-telegram-bg">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Nhập tin nhắn..."
            className="flex-1 bg-telegram-secondary rounded-full px-4 py-2 outline-none"
            disabled={!isConnected}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || !isConnected}
            className="bg-telegram-button text-telegram-button-text rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-50"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}
